// CursorEditorPopup — Classic の「カーソル」フロート (custom-cursor-2026-09-19.md
// §2.5 エディタ)。位置・ドラッグ移動は `PenPopup.tsx` と同じ仕組み
// (`usePanelDragGrip` + `floatShellGeometry`) を流用する (= 独自のウィンドウ実装を
// しない)。中身 (グリッド・ツール・決定/やめる/外す) は Modern とも共有する
// `CursorEditorPanel` (presentation/layouts/shared/)。
//
// 開閉は ClassicLayout が boolean state で持ち (`ToolPanel` の「カーソル」トグル
// ボタンから)、本 popup 内の「決定」「やめる」「カーソルを外す」いずれかを押すと
// 閉じる (= PenPopup と違い常設ではなく、押した時だけ使う一時的な作業窓なので
// ヘッダに ✕ は付けず、パネル内アクションで閉じる一本化)。

import type { CustomCursor } from '@oekaki/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CursorEditorPanel } from '../shared/CursorEditorPanel.js';
import {
  clampFloatPosition,
  type FloatPoint,
  floatPositionFromPointer,
} from '../shared/floatShellGeometry.js';
import { useCursorEditorCellPxResize } from '../shared/useCursorEditorCellPxResize.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import type { SidebarDockSide } from './classicToolFloat.js';
import {
  CURSOR_EDITOR_POPUP_NOMINAL_HEIGHT,
  CURSOR_EDITOR_POPUP_NOMINAL_WIDTH,
  defaultCursorEditorPopupPosition,
  readCursorEditorPopupPosition,
  writeCursorEditorPopupPosition,
} from './cursorEditorPopup.js';
import type { FloatingPopupPosition } from './layerPopup.js';
import { insetFrame, outsetFrame } from './legacyFrames.js';

/** 旧 `clampPopupPosition` の既定 margin (8px) と揃える (= PenPopup と同じ). */
const POPUP_EDGE_INSET_PX = 8;

export interface CursorEditorPopupProps {
  /** 閉じる (= 「決定」「やめる」「カーソルを外す」いずれかの後、ClassicLayout が呼ぶ). */
  readonly onClose: () => void;
  readonly initialCursor: CustomCursor | null;
  readonly currentColor: { readonly rgb24: number; readonly alpha: number };
  readonly spuitMode: boolean;
  readonly onSpuitPick: (color: { rgb24: number; alpha: number }) => void;
  readonly onDraft: (cursor: CustomCursor | null) => void;
  readonly onConfirm: (cursor: CustomCursor) => void;
  readonly onRemove: () => void;
  /** サイドバードック側 (PenPopup/LayerPopup と同じミラー方針). */
  readonly dockSide?: SidebarDockSide;
  /**
   * 自分のカーソルを自分の画面にも表示するか (作者 staging フィードバック
   * 2026-09-19 follow-up、`PenSettingsPanel` と同じ共有 chrome 値)。省略時は
   * `CursorEditorPanel` 側の既定 (= チェックボックス自体を出さない) に従う。
   */
  readonly showOwnCustomCursor?: boolean;
  readonly onShowOwnCustomCursorChange?: (next: boolean) => void;
}

export const CursorEditorPopup = ({
  onClose,
  initialCursor,
  currentColor,
  spuitMode,
  onSpuitPick,
  onDraft,
  onConfirm,
  onRemove,
  dockSide = 'left',
  showOwnCustomCursor,
  onShowOwnCustomCursorChange,
}: CursorEditorPopupProps): React.ReactNode => {
  // 作者 staging フィードバック 2026-09-19 follow-up「パネルをリサイズして大きく
  // したい」: プリセットボタン (小/中/大、CursorEditorPanel 内) とこの popup の
  // 右下角ドラッグの両方が同じ `cellPx` state を更新する (= 1 本の共有 hook)。
  const { cellPx, setCellPx, resizeGrip } = useCursorEditorCellPxResize();
  const [pos, setPos] = useState<FloatingPopupPosition>(() => {
    if (typeof window === 'undefined') return defaultCursorEditorPopupPosition(0, 0, dockSide);
    const stored = readCursorEditorPopupPosition(window.localStorage);
    if (stored !== null) return stored;
    return defaultCursorEditorPopupPosition(window.innerWidth, window.innerHeight, dockSide);
  });
  const panelRef = useRef<HTMLElement | null>(null);

  const measurePanel = useCallback((): { width: number; height: number } => {
    const el = panelRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    return { width: CURSOR_EDITOR_POPUP_NOMINAL_WIDTH, height: CURSOR_EDITOR_POPUP_NOMINAL_HEIGHT };
  }, []);

  const persistPosition = useCallback((next: FloatingPopupPosition): void => {
    if (typeof window === 'undefined') return;
    writeCursorEditorPopupPosition(window.localStorage, next);
  }, []);

  const grabOffsetRef = useRef<FloatPoint | null>(null);
  const grip = usePanelDragGrip({
    onDragStart: (offset) => {
      grabOffsetRef.current = offset;
    },
    onDragMove: (clientX, clientY) => {
      const offset = grabOffsetRef.current ?? { x: 0, y: 0 };
      setPos(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measurePanel(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    },
    onDragEnd: (committed) => {
      grabOffsetRef.current = null;
      if (committed) {
        setPos((cur) => {
          persistPosition(cur);
          return cur;
        });
      }
    },
    measureGrabOffset: (e) => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  // 初回 clamp: 復元位置が小さい画面ではみ出すケースを mount 後に画面内へ引き戻す。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPos((cur) => {
      const clamped = clampFloatPosition(
        cur,
        measurePanel(),
        { width: window.innerWidth, height: window.innerHeight },
        POPUP_EDGE_INSET_PX,
      );
      if (clamped.x !== cur.x || clamped.y !== cur.y) persistPosition(clamped);
      return clamped;
    });
  }, [measurePanel, persistPosition]);

  const handleConfirm = (cursor: CustomCursor): void => {
    onConfirm(cursor);
    onClose();
  };
  const handleRemove = (): void => {
    onRemove();
    onClose();
  };

  return (
    <section
      ref={panelRef}
      data-testid="classic-cursor-editor-popup"
      aria-label="カーソル"
      className={`fixed z-40 flex flex-col bg-[var(--color-panel)] text-white select-none ${outsetFrame}`}
      // 作者 staging フィードバック 2026-09-19 follow-up「リサイズしたい」: 固定
      // 幅から min-width へ (= cellPx を大きくするとグリッドに合わせて広がる)。
      style={{ left: pos.x, top: pos.y, minWidth: CURSOR_EDITOR_POPUP_NOMINAL_WIDTH }}
    >
      <div
        data-testid="classic-cursor-editor-popup-header"
        className="flex shrink-0 cursor-move items-center justify-between border-b border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[10px] font-bold"
        style={{ touchAction: 'none' }}
        onPointerDown={grip.onPointerDown}
        onPointerMove={grip.onPointerMove}
        onPointerUp={grip.onPointerUp}
        onPointerCancel={grip.onPointerCancel}
        onLostPointerCapture={grip.onLostPointerCapture}
      >
        <span>カーソル</span>
      </div>
      <div className={`m-1 p-1 ${insetFrame}`}>
        <CursorEditorPanel
          initialCursor={initialCursor}
          currentColor={currentColor}
          spuitMode={spuitMode}
          onSpuitPick={onSpuitPick}
          onDraft={onDraft}
          onConfirm={handleConfirm}
          onCancel={onClose}
          onRemove={handleRemove}
          cellPx={cellPx}
          onCellPxChange={setCellPx}
          showOwnCustomCursor={showOwnCustomCursor}
          onShowOwnCustomCursorChange={onShowOwnCustomCursorChange}
        />
      </div>
      {/* 作者 staging フィードバック 2026-09-19 follow-up「パネルをリサイズして
          大きくしたい」: 右下角ドラッグでグリッドを拡縮する (= プリセットボタンと
          同じ `cellPx` state、`useCursorEditorCellPxResize` 経由)。pointer-capture
          の配管自体はドラッグ移動と同じ `usePanelDragGrip` を再利用する。 */}
      <div
        data-testid="classic-cursor-editor-popup-resize-grip"
        role="presentation"
        title="ドラッグでマスの大きさを変更"
        className="absolute right-0 bottom-0 h-4 w-4 cursor-nwse-resize opacity-60 hover:opacity-100"
        style={{
          touchAction: 'none',
          backgroundImage:
            'linear-gradient(135deg, transparent 0 45%, var(--color-border) 45% 55%, transparent 55% 100%), linear-gradient(135deg, transparent 0 70%, var(--color-border) 70% 80%, transparent 80% 100%)',
        }}
        onPointerDown={resizeGrip.onPointerDown}
        onPointerMove={resizeGrip.onPointerMove}
        onPointerUp={resizeGrip.onPointerUp}
        onPointerCancel={resizeGrip.onPointerCancel}
        onLostPointerCapture={resizeGrip.onLostPointerCapture}
      />
    </section>
  );
};
